import { useMemo, useState, type JSX } from 'react';
import { EMPTY, useEngine } from '../../state/engineStore';
import { PRI_COLOUR, PriorityMark, stateLabel } from './AlarmBanner';
import { fmtSimTime } from '../shell/format';
import type { AlarmSnapshot } from '../../engine/alarms/alarms';

const SHELVE_REASONS = [
  'Known instrument fault — work order raised',
  'Equipment out of service for maintenance',
  'Nuisance alarm during a transient',
  'Under investigation by the day team',
];

export function AlarmSummary(): JSX.Element {
  const alarms = useEngine((s) => s.snap?.alarms ?? (EMPTY as unknown as AlarmSnapshot[]));
  const metrics = useEngine((s) => s.snap?.alarmMetrics);
  const plant = useEngine((s) => s.plant);
  const send = useEngine((s) => s.send);
  const [filter, setFilter] = useState<'all' | 'unacked' | 'shelved'>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [shelving, setShelving] = useState<string | null>(null);
  const [shelveMins, setShelveMins] = useState(30);
  const [shelveReason, setShelveReason] = useState(SHELVE_REASONS[0] as string);

  const rows = useMemo(
    () =>
      alarms.filter((a) =>
        filter === 'unacked'
          ? a.state === 'unackedActive' || a.state === 'unackedRtn'
          : filter === 'shelved'
            ? a.shelved || a.oos
            : true,
      ),
    [alarms, filter],
  );

  const detail = alarms.find((a) => a.id === selected) ?? null;
  const guidance = plant?.alarms.find((a) => a.id === selected);

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid var(--line-dim)' }}>
          {(['all', 'unacked', 'shelved'] as const).map((f) => (
            <button key={f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'unacked' ? 'Unacknowledged' : 'Shelved / OOS'}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {metrics && (
            <span className="muted num" style={{ fontSize: 11.5 }}>
              {metrics.per10min} per 10 min{' '}
              <span style={{ color: metrics.per10min > 1 ? 'var(--pri-medium)' : undefined }}>
                (EEMUA target ≤ 1)
              </span>{' '}
              · {metrics.standing} standing · {metrics.unackedMinutes.toFixed(1)} unack-min
              {metrics.inFlood && <strong style={{ color: 'var(--pri-emergency)' }}> · FLOOD</strong>}
            </span>
          )}
          <button onClick={() => send({ t: 'ackAll' })}>Ack page</button>
        </div>

        <div className="scroll" style={{ flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--panel-2)', textAlign: 'left' }}>
                <th style={{ padding: '5px 8px', width: 26 }} />
                <th style={{ padding: '5px 8px' }}>Time</th>
                <th style={{ padding: '5px 8px' }}>Tag</th>
                <th style={{ padding: '5px 8px' }}>Description</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>Value</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>Limit</th>
                <th style={{ padding: '5px 8px' }}>State</th>
                <th style={{ padding: '5px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                    Nothing to show.
                  </td>
                </tr>
              )}
              {rows.map((a) => {
                const unacked = a.state === 'unackedActive' || a.state === 'unackedRtn';
                return (
                  <tr
                    key={a.id}
                    onClick={() => setSelected(a.id)}
                    style={{
                      borderBottom: '1px solid var(--panel-2)',
                      background: selected === a.id ? 'var(--panel-2)' : undefined,
                      cursor: 'pointer',
                      opacity: a.shelved || a.oos ? 0.55 : 1,
                    }}
                  >
                    <td style={{ padding: '4px 8px' }}>
                      <span className={unacked ? 'flash' : undefined} style={{ display: 'inline-flex' }}>
                        <PriorityMark priority={a.priority} />
                      </span>
                    </td>
                    <td className="num muted" style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                      {fmtSimTime(a.since)}
                    </td>
                    <td className="num" style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                      {a.tag}
                    </td>
                    <td style={{ padding: '4px 8px' }}>{a.text}</td>
                    <td className="num" style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {a.value.toFixed(2)}
                    </td>
                    <td className="num muted" style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {a.limit === null ? '—' : a.limit.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: '4px 8px',
                        whiteSpace: 'nowrap',
                        color: unacked ? PRI_COLOUR[a.priority] : 'var(--text-mute)',
                        fontWeight: unacked ? 600 : 400,
                      }}
                    >
                      {stateLabel(a)}
                    </td>
                    <td style={{ padding: '2px 8px', whiteSpace: 'nowrap' }}>
                      {unacked && (
                        <button style={{ padding: '1px 7px' }} onClick={(e) => { e.stopPropagation(); send({ t: 'ackAlarm', ids: [a.id] }); }}>
                          Ack
                        </button>
                      )}{' '}
                      {a.shelved ? (
                        <button style={{ padding: '1px 7px' }} onClick={(e) => { e.stopPropagation(); send({ t: 'unshelve', id: a.id }); }}>
                          Unshelve
                        </button>
                      ) : (
                        <button style={{ padding: '1px 7px' }} onClick={(e) => { e.stopPropagation(); setShelving(a.id); }}>
                          Shelve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <aside
        className="scroll hmi-aside"
        style={{ width: 330, borderLeft: '1px solid var(--line-dim)', padding: 12, background: 'var(--panel)' }}
      >
        {detail ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
              <PriorityMark priority={detail.priority} size={16} />
              <strong>{detail.text}</strong>
            </div>
            <div className="num muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
              {detail.id} · {detail.tag} · {detail.kind}
            </div>
            {detail.shelved && (
              <div style={{ marginBottom: 10, fontSize: 11.5, color: 'var(--pri-medium)' }}>
                Shelved: {detail.shelveReason}
              </div>
            )}
            {guidance && (
              <dl style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
                <dt style={{ fontWeight: 600, marginTop: 8 }}>Likely cause</dt>
                <dd style={{ margin: '2px 0 0' }} className="muted">
                  {guidance.cause}
                </dd>
                <dt style={{ fontWeight: 600, marginTop: 10 }}>Consequence</dt>
                <dd style={{ margin: '2px 0 0' }} className="muted">
                  {guidance.consequence}
                </dd>
                <dt style={{ fontWeight: 600, marginTop: 10 }}>Corrective action</dt>
                <dd style={{ margin: '2px 0 0' }}>{guidance.action}</dd>
              </dl>
            )}
          </>
        ) : (
          <div className="muted">Select an alarm to see its cause, consequence and corrective action.</div>
        )}
      </aside>

      {shelving && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 500,
          }}
          onClick={() => setShelving(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--panel)', border: '1px solid var(--line-dim)', borderRadius: 5, padding: 16, width: 420 }}
          >
            <h3 style={{ margin: '0 0 10px' }}>Shelve {shelving}</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
              A shelved alarm stops annunciating and stops counting toward the alarm-rate metrics. It
              un-shelves itself when the timer expires. A reason is mandatory and is written to the
              event log.
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Reason
              <select
                value={shelveReason}
                onChange={(e) => setShelveReason(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              >
                {SHELVE_REASONS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: 14, fontSize: 12 }}>
              Duration (minutes of simulated time)
              <input
                type="number"
                value={shelveMins}
                min={1}
                max={480}
                onChange={(e) => setShelveMins(Number(e.target.value))}
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShelving(null)}>Cancel</button>
              <button
                className="on"
                onClick={() => {
                  send({ t: 'shelve', id: shelving, minutes: shelveMins, reason: shelveReason });
                  setShelving(null);
                }}
              >
                Shelve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
